import { d, round, type DecimalLike } from './decimal';
import { getCurrency, type CurrencyDisplay } from './currencies';

/**
 * Deterministic number/money formatting. Intl output differs between browsers and the
 * Workers runtime, which would make previews and PDFs diverge, so formatting is done here.
 */
export type NumberFormatId = 'en' | 'eu' | 'fr' | 'ch' | 'in' | 'plain';

export interface NumberFormatSpec {
  id: NumberFormatId;
  label: string;
  group: string;
  decimal: string;
  grouping: 'standard' | 'indian' | 'none';
}

export const NUMBER_FORMATS: Record<NumberFormatId, NumberFormatSpec> = {
  en: { id: 'en', label: '1,234,567.89', group: ',', decimal: '.', grouping: 'standard' },
  eu: { id: 'eu', label: '1.234.567,89', group: '.', decimal: ',', grouping: 'standard' },
  fr: { id: 'fr', label: '1 234 567,89', group: '\u00a0', decimal: ',', grouping: 'standard' },
  ch: { id: 'ch', label: "1'234'567.89", group: "'", decimal: '.', grouping: 'standard' },
  in: { id: 'in', label: '12,34,567.89', group: ',', decimal: '.', grouping: 'indian' },
  plain: { id: 'plain', label: '1234567.89', group: '', decimal: '.', grouping: 'none' },
};

export const NUMBER_FORMAT_IDS = Object.keys(NUMBER_FORMATS) as NumberFormatId[];

function groupDigits(integer: string, spec: NumberFormatSpec): string {
  if (spec.grouping === 'none' || integer.length <= 3) return integer;
  if (spec.grouping === 'indian') {
    const last3 = integer.slice(-3);
    let rest = integer.slice(0, -3);
    const parts: string[] = [];
    while (rest.length > 2) {
      parts.unshift(rest.slice(-2));
      rest = rest.slice(0, -2);
    }
    if (rest) parts.unshift(rest);
    return [...parts, last3].join(spec.group);
  }
  const parts: string[] = [];
  let rest = integer;
  while (rest.length > 3) {
    parts.unshift(rest.slice(-3));
    rest = rest.slice(0, -3);
  }
  if (rest) parts.unshift(rest);
  return parts.join(spec.group);
}

export interface FormatNumberOptions {
  format?: NumberFormatId;
  /** Remove trailing zeros down to `minDecimals`. */
  trimZeros?: boolean;
  minDecimals?: number;
}

export function formatNumber(
  value: DecimalLike,
  decimals: number,
  options: FormatNumberOptions = {},
): string {
  const spec = NUMBER_FORMATS[options.format ?? 'en'] ?? NUMBER_FORMATS.en;
  const rounded = round(value, decimals);
  const negative = rounded.lt(0);
  let fixed = rounded.abs().toFixed(decimals);
  if (options.trimZeros && fixed.includes('.')) {
    const minDecimals = options.minDecimals ?? 0;
    const [intPart = '0', fracPart = ''] = fixed.split('.');
    let frac = fracPart.replace(/0+$/, '');
    if (frac.length < minDecimals) frac = frac.padEnd(minDecimals, '0');
    fixed = frac ? `${intPart}.${frac}` : intPart;
  }
  const [intPart = '0', fracPart] = fixed.split('.');
  const body = groupDigits(intPart, spec) + (fracPart ? spec.decimal + fracPart : '');
  return negative ? `-${body}` : body;
}

export function formatQuantity(value: DecimalLike, format: NumberFormatId = 'en', maxDecimals = 4): string {
  return formatNumber(value, maxDecimals, { format, trimZeros: true });
}

export function formatPercent(value: DecimalLike, format: NumberFormatId = 'en', maxDecimals = 3): string {
  return `${formatNumber(value, maxDecimals, { format, trimZeros: true })}%`;
}

export interface FormatMoneyOptions {
  format?: NumberFormatId;
  display?: CurrencyDisplay;
  /** Override currency precision (e.g. unit prices with extra precision). */
  decimals?: number;
  trimZeros?: boolean;
}

export function formatMoney(
  value: DecimalLike,
  currencyCode: string,
  options: FormatMoneyOptions = {},
): string {
  const currency = getCurrency(currencyCode);
  const decimals = options.decimals ?? currency.decimals;
  const amount = d(value);
  const negative = round(amount, decimals).lt(0);
  const number = formatNumber(amount.abs(), decimals, {
    format: options.format,
    trimZeros: options.trimZeros,
    minDecimals: options.trimZeros ? currency.decimals : undefined,
  });
  const display = options.display ?? 'symbol';
  let result: string;
  if (display === 'code') {
    result = `${currency.code}\u00a0${number}`;
  } else {
    const symbol = display === 'narrow' ? currency.narrowSymbol : currency.symbol;
    const space = currency.symbolSpacing ? '\u00a0' : '';
    result =
      currency.symbolPosition === 'after' ? `${number}${space}${symbol}` : `${symbol}${space}${number}`;
  }
  return negative ? `-${result}` : result;
}

/** Compact money for dashboards, e.g. "$12.4K". */
export function formatCompactMoney(
  value: DecimalLike,
  currencyCode: string,
  format: NumberFormatId = 'en',
): string {
  const amount = d(value);
  const abs = amount.abs();
  const units: Array<[number, string]> = [
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];
  for (const [threshold, suffix] of units) {
    if (abs.gte(threshold)) {
      const currency = getCurrency(currencyCode);
      const scaled = formatNumber(amount.div(threshold), 1, { format, trimZeros: true });
      const space = currency.symbolSpacing ? '\u00a0' : '';
      return currency.symbolPosition === 'after'
        ? `${scaled}${suffix}${space}${currency.narrowSymbol}`
        : `${currency.narrowSymbol}${space}${scaled}${suffix}`;
    }
  }
  return formatMoney(amount, currencyCode, { format, display: 'narrow' });
}
